import React, {useMemo, useState} from 'react';
import {Button, Container, Grid, TextField, withStyles} from "@material-ui/core";
import styled from 'styled-components';
import Layout from '../Shared/Layout';
import {InertiaLink, useForm} from '@inertiajs/inertia-react';
import {Trans} from "../utils/i18n";
import {useTheme} from "@mui/material";
import {useLanguageSwitch} from "../hooks/useLanguageSwitch";
import { useShoppingCartStore } from '../stores/ShoppingCartStore';

const Wrapper = styled.div`
  width: 100%;
`;

const Card = styled.div`
  padding: 50px;
  color: ${props => props.theme.white};
  background-color: ${props => props.theme.primary};
  text-align: center;
  border-radius: .7rem;
`;

const InputTextField = styled(TextField)`
  input {
    background: ${props => props.theme.white};
    border-radius: 4px 4px 0 0;
  }
`;

const PasswordResetLink = styled(InertiaLink)`
  color: ${props => props.theme.white};
  text-decoration: none;
  display: block;
`;

export default function Login({ client, openIDAvailable, errors, openIdLoginLink, }) {
    const [twoFaWasSend, setTwoFaWasSend] = useState(false);
    const { updateLanguage } = useLanguageSwitch();
    const { data, setData, post, processing, errors: formErrors, reset } = useForm({
        email: '',
        password: '',
        remember: false,
    });
    const theme = useTheme();
    const store = useShoppingCartStore();

    const colorCollision = theme.palette.primary.main === theme.palette.secondary.main;

    const buttonColor = useMemo(() => {
        if(!colorCollision) {
            return theme.palette.primary.main;
        }
        return theme.palette.primary.contrastText;
    }, []);

    errors = {
        ...errors,
        ...formErrors,
    };

    const submit = (e) => {
        e.preventDefault();

        post(route('login'), {
            onError: () => {
                if(data['2fa_code']) {
                    setTwoFaWasSend(true);
                }
            },
            onSuccess: (data) => {
                updateLanguage(data?.props?.user?.language);
                store.send({
                    type: "clearCart"
                });
            }
        });
    };
    const StyledButton = colorCollision ? withStyles({
        root: {
            background: buttonColor,
            color: theme.palette.primary.main,
            '&:hover': {
                backgroundColor: theme.palette.primary.light,
                color: buttonColor,
            },
        },
    })(Button) : Button;

  return (
    <Wrapper>
      <Container maxWidth="lg">
        <Grid container justify="center">
          <Grid item xs={12} sm={6} md={4}>
            <Card>
              <h2 className={'mt-0 mb-4'}><Trans i18nKey="login">Login</Trans></h2>
              <form onSubmit={submit}>
                  {
                    !errors?.['2fa_code'] && (
                        <>
                            <div className={'mb-3'}>
                                <InputTextField
                                    id="email"
                                    label={<Trans i18nKey="login.email">E-Mail</Trans>}
                                    name="email"
                                    value={data.email}
                                    type="email"
                                    required
                                    variant="filled"
                                    onChange={(e) => setData('email', e.target.value)}
                                    disabled={processing}
                                />
                            </div>
                            <div className={'mb-3'}>
                                <InputTextField
                                    id="password"
                                    autoComplete="current-password"
                                    label={<Trans i18nKey="login.password">Password</Trans>}
                                    name="password"
                                    value={data.password}
                                    type="password"
                                    required
                                    error={!!errors.email}
                                    helperText={errors.email}
                                    variant="filled"
                                    onChange={(e) => setData('password', e.target.value)}
                                    disabled={processing}
                                />
                            </div>

                            <label className="relative flex items-start mb-6">
                                <div className="flex h-6 items-center">
                                    <input
                                        id="remember"
                                        aria-describedby="remember-description"
                                        type="checkbox"
                                        className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                                        name="remember"
                                        checked={data.remember}
                                        onChange={(e) => setData('remember', e.target.checked)}
                                        disabled={processing}
                                    />
                                </div>
                                <div className="ml-3 text-sm leading-6">
                                    <span className="font-medium text-gray-200">
                                        <Trans i18nKey="login.rememberMe">Remember me</Trans>
                                    </span>
                                </div>
                            </label>

                                <div className={'flex flex-col gap-6'}>
                                    <StyledButton type="submit" variant="contained" color="secondary" className={'w-full'}>
                                        {
                                            processing ? (
                                                <svg className="animate-spin -ml-1 mr-3 h-6 w-6 text-white"
                                                     xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10"
                                                            stroke="currentColor" strokeWidth="4"></circle>
                                                    <path className="opacity-75" fill="currentColor"
                                                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                </svg>
                                            )
                                            :
                                                <Trans i18nKey="login">Login</Trans>
                                        }
                                    </StyledButton>
                                    <div>
                                        <PasswordResetLink href={route('password.request')} ><Trans i18nKey="login.forgotMyPassword">I forgot my password</Trans></PasswordResetLink>
                                    </div>
                                </div>
                                {
                                    client.self_registration == true &&
                                    <div style={{ marginTop: 30,  justifyContent:'center', alignItems:'center' }} ><PasswordResetLink href={route('register')}><Trans i18nKey="login.register">Register new account</Trans></PasswordResetLink></div>
                                }
                              </>
                      )
                  }
                  {
                      errors?.['2fa_code'] && (
                          <>
                              <InputTextField
                                  id="2fa_code"
                                  label={<Trans i18nKey="login.2fa_code">Two Factor Code</Trans>}
                                  name="2fa_code"
                                  value={data['2fa_code']}
                                  type="number"
                                  required
                                  error={!!errors['2fa_code'] && twoFaWasSend}
                                  helperText={twoFaWasSend && errors['2fa_code']}
                                  variant="filled"
                                  style={{ marginBottom: '25px' }}
                                  onChange={(e) => setData('2fa_code', e.target.value)}
                              />
                              <div className={'flex items-end'}>
                                  <Button type="submit" variant="contained" color="secondary"><Trans i18nKey="login">Login</Trans></Button>
                              </div>
                          </>
                      )
                  }
              </form>
            </Card>
              {
                  openIDAvailable &&
                  <div className={'mt-6 px-6 py-3 text-white bg-primary text-center rounded-lg'}>
                      <h2>
                          <Trans i18nKey="login.loginWithOpenIdQuestion">
                              Login with OpenID ?
                          </Trans>
                      </h2>
                      <div className={'mb-3'}>
                          <Button
                              color={'secondary'}
                              variant={'contained'}
                              component={'a'}
                              href={openIdLoginLink}
                          >
                              <Trans i18nKey="login.goToOpenID">
                                  Go to OpenID Login
                              </Trans>
                          </Button>
                      </div>
                  </div>
              }
          </Grid>
        </Grid>
      </Container>
    </Wrapper>
  );
}

Login.layout = page => <Layout children={page} />;
